import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { AppIconComponent } from '../../lib/ui';

export interface Category {
  id: number;
  name: string;
  description: string;
  active: boolean;
  productCount: number;
  createdAt: Date;
}

/**
 * Categories management component - modernized with Angular 20 patterns
 * Uses signals for reactive state management and Tailwind CSS for styling
 */
@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppIconComponent],
})
export class CategoriesComponent implements OnInit {
  private fb = inject(FormBuilder);

  // Signals for reactive state management
  categories = signal<Category[]>([]);
  isLoading = signal(false);

  // Filter form
  filterForm = this.fb.nonNullable.group({
    search: [''],
  });

  // Computed signals for derived state
  filteredCategories = computed(() => {
    const categoriesData = this.categories();
    const searchTerm = this.filterForm.value.search?.toLowerCase() || '';

    if (!searchTerm) {
      return categoriesData;
    }

    return categoriesData.filter(
      (category) =>
        category.name.toLowerCase().includes(searchTerm) ||
        category.description.toLowerCase().includes(searchTerm)
    );
  });

  // Stats computed signals
  totalCategories = computed(() => this.categories().length);

  activeCategories = computed(
    () => this.categories().filter((category) => category.active).length
  );

  inactiveCategories = computed(
    () => this.categories().filter((category) => !category.active).length
  );

  resultCount = computed(() => this.filteredCategories().length);

  showEmptyState = computed(
    () => !this.isLoading() && this.filteredCategories().length === 0
  );

  ngOnInit() {
    this.loadCategories();
    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions() {
    // Watch for form changes to trigger filtering
    this.filterForm.valueChanges.subscribe(() => {
      // Filtering is automatic through computed signals
    });
  }

  loadCategories() {
    this.isLoading.set(true);

    // Simulating API call with mock data
    setTimeout(() => {
      const mockCategories: Category[] = [
        {
          id: 1,
          name: 'Eletrônicos',
          description: 'Produtos eletrônicos em geral',
          active: true,
          productCount: 25,
          createdAt: new Date('2024-01-15'),
        },
        {
          id: 2,
          name: 'Roupas e Acessórios',
          description: 'Vestuário e acessórios de moda',
          active: true,
          productCount: 18,
          createdAt: new Date('2024-02-10'),
        },
        {
          id: 3,
          name: 'Casa e Jardim',
          description: 'Produtos para casa e jardim',
          active: true,
          productCount: 12,
          createdAt: new Date('2024-03-05'),
        },
        {
          id: 4,
          name: 'Esporte e Lazer',
          description: 'Artigos esportivos e de lazer',
          active: false,
          productCount: 5,
          createdAt: new Date('2024-03-20'),
        },
        {
          id: 5,
          name: 'Livros e Mídia',
          description: 'Livros, filmes e produtos de mídia',
          active: true,
          productCount: 8,
          createdAt: new Date('2024-04-01'),
        },
        {
          id: 6,
          name: 'Automotivo',
          description: 'Peças e acessórios automotivos',
          active: false,
          productCount: 3,
          createdAt: new Date('2024-04-15'),
        },
      ];

      this.categories.set(mockCategories);
      this.isLoading.set(false);
    }, 800);
  }

  // Category actions
  editCategory(category: Category) {
    console.log('Editing category:', category);
    // TODO: Open edit dialog or navigate to edit page
  }

  deleteCategory(category: Category) {
    console.log('Deleting category:', category);
    // TODO: Show confirmation dialog and delete
  }

  addCategory() {
    console.log('Adding new category');
    // TODO: Open add category dialog or navigate to add page
  }

  toggleStatus(category: Category) {
    // Create a new array with the updated category
    const updatedCategories = this.categories().map((c) =>
      c.id === category.id ? { ...c, active: !c.active } : c
    );
    this.categories.set(updatedCategories);
    console.log('Toggling category status:', category);
    // TODO: Update category status in backend
  }

  // Utility methods
  getStatusClass(active: boolean): string {
    return active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }

  getStatusText(active: boolean): string {
    return active ? 'Ativa' : 'Inativa';
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }
}
